import { AxiosInstance } from 'axios';
import qs from 'qs';

import { OffsetPaginationInput } from '@app/data/inputs';
import { CollectionResource } from '@app/data/resources';

import { CoreUserFilterInput, CreateCoreUserInput, SetCoreUserOccupancySettings, SetCoreUserRoleInput, TimeTrackingEntrySortField } from './inputs';
import { CoreUserRef } from './refs';
import {
    CoreUserListItemResource,
    CoreUserResource,
    TaskTimeTrackingEntryResource,
    UserOccupancySettingsResource,
} from './resources';

export default class CoreUser {
    private basePath = '/api/core-user';
    private http: AxiosInstance;

    constructor(http: AxiosInstance) {
        this.http = http;
    }

    // Get core user list
    get = async (
        pagination: OffsetPaginationInput,
        filter?: CoreUserFilterInput
    ): Promise<CollectionResource<CoreUserListItemResource, {}>> => {
        const query = qs.stringify({ ...pagination, ...(filter || {}) });

        const { data } = await this.http.get(`${this.basePath}/?${query}`);

        return data;
    };

    // Get single core user
    getById = async (id: ID): Promise<CoreUserResource> => {
        const { data } = await this.http.get(`${this.basePath}/${id}`);

        return data;
    };

    // Get user occupancy settings (may be 404)
    getOccupancySettings = async (id: ID): Promise<UserOccupancySettingsResource> => {
        const { data } = await this.http.get(`${this.basePath}/${id}/occupancy-settings`);
        return data;
    };

    // Create new core user
    create = async (input: CreateCoreUserInput): Promise<CoreUserRef> => {
        const { data } = await this.http.post(`${this.basePath}/`, input);

        return data;
    };

    // Update core user occupancy settings
    updateOccupancySettings = async (id: ID, input: SetCoreUserOccupancySettings): Promise<CoreUserRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/occupancy-settings`, input);

        return data;
    };

    // Update core user occupancy settings
    setRole = async (id: ID, input: SetCoreUserRoleInput): Promise<CoreUserRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/role`, input);

        return data;
    };

    // Last time tracking entries for core user, grouped by task
    getTimeTrackingEntries = async (
        id: ID,
        pagination: OffsetPaginationInput<TimeTrackingEntrySortField>
    ): Promise<CollectionResource<TaskTimeTrackingEntryResource, TimeTrackingEntrySortField>> => {
        const query = qs.stringify({ ...pagination });

        const { data } = await this.http.get(`${this.basePath}/${id}/time-tracking/entry/task/?${query}`);

        return data;
    };
}
